import React, { useMemo, useState } from 'react';
import { Container, Card, Button, Form as BootstrapForm, Row, Col } from 'react-bootstrap';
import { Select, Spin } from 'antd';
import { FilterOutlined, LoadingOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import FormItem from '@/common/FormItem/FormItem';
import PageTitle from '@/common/PageTitle/PageTitle';
import { getChannelArtwork, DEFAULT_ALBUM_ART } from '@/utils/artwork';
import musicVendorStyles from './music-vendor-roles.module.scss';
import styles from './channel-programming-overview.module.scss';
import { useListChannelsQuery } from '@/features/channels/channelsApi';
import { toUiStatus } from '@/features/channels/channelStatus';

/**
 * CHANNEL PROGRAMMING OVERVIEW - IMPORTANT FOR FUTURE DEVELOPMENT:
 *
 * This is the overview page showing all channels with filters and cards.
 * For the detailed programming interface (individual channel), see: channel-programming-interface.tsx
 *
 * This site uses a HYBRID approach for UI components:
 * - BOOTSTRAP for layout: Row, Col, Container, Card, Button, Form (as BootstrapForm)
 * - ANT DESIGN only for specific components: Select, Modal, Tooltip, Checkbox, Icons
 *
 * CRITICAL: Never mix Bootstrap Row/Col with Ant Design Row/Col!
 * - Use Bootstrap: <Col md={4}> (Bootstrap syntax)
 * - NOT Ant Design: <Col span={8}> (Ant Design syntax)
 *
 * For filters, use Bootstrap Form + FormItem + Ant Design Select components.
 * Reference: music-vendor-roles.module.scss for proper filter styling.
 */

const ChannelProgrammingOverview: React.FC = () => {
  const navigate = useNavigate();
  const { data, isLoading, isFetching } = useListChannelsQuery();

  const channels = useMemo(
    () =>
      (data?.items || []).map((ch) => ({
        id: ch.channelId,
        name: ch.name,
        description: ch.description || '',
        status: toUiStatus(ch.status),
        artwork: ch.artworkUrl || DEFAULT_ALBUM_ART,
        channelType: ch.channelType || '',
      })),
    [data],
  );

  const [filters, setFilters] = useState({
    channelName: undefined as string | undefined,
    status: undefined as string | undefined,
    type: undefined as string | undefined,
  });
  const [resetCounter, setResetCounter] = useState(0);

  const filterOptions = useMemo(() => {
    const channelNames = Array.from(new Set(channels.map((c) => c.name)));
    const status = Array.from(new Set(channels.map((c) => c.status)));
    const types = ['Original', 'Commercial'];
    return { channelNames, status, types };
  }, [channels]);

  const handleFilterChange = (filterName: string, value: string | undefined) => {
    setFilters(prev => ({ ...prev, [filterName]: value }));
  };

  const handleReset = () => {
    setFilters({
      channelName: undefined,
      status: undefined,
      type: undefined,
    });
    setResetCounter(prev => prev + 1);
  };

  const filteredChannels = channels.filter(channel => {
    const matchesChannelName = !filters.channelName ||
      channel.name.toLowerCase().includes(filters.channelName.toLowerCase());

    const matchesStatus = !filters.status ||
      channel.status.toLowerCase() === filters.status.toLowerCase();

    const typeLabel = (channel.channelType || '').replace(/Exclusive/gi, 'Original');
    const matchesType = !filters.type ||
      (filters.type === 'Original' &&
        (typeLabel.toLowerCase().includes('original') ||
          channel.name.includes('KK Original') ||
          channel.name.includes('KK Exclusive'))) ||
      (filters.type === 'Commercial' &&
        (channel.channelType.toLowerCase().includes('commercial') ||
          (!channel.name.includes('KK Exclusive') && !channel.name.includes('KK Original'))));

    return matchesChannelName && matchesStatus && matchesType;
  });

  return (
    <Container fluid>
      <Card>
        <Card.Body>
          <Card.Title style={{ display: 'flex', justifyContent: 'space-between' }}>
            <div>
              <PageTitle title="Channel Programming Overview" />
              <p style={{
                color: '#7f8c8d',
                fontSize: '14px',
                margin: '4px 0 0 0',
                fontWeight: '400'
              }}>
                Choose a channel to program music and manage rotations
              </p>
            </div>
            <Button
              onClick={handleReset}
              style={{
                background: 'white',
                color: '#333',
                border: '1px solid #e0e0e0',
                borderRadius: '6px',
                padding: '8px 16px',
                fontWeight: '500'
              }}
            >
              <FilterOutlined /> Reset All
            </Button>
          </Card.Title>

          <BootstrapForm className={musicVendorStyles.form}>
            <Row>
              <Col>
                <Row className="mb-2">
                  <Col>
                    <Row>
                      <Col md={4}>
                        <FormItem
                          label="Channel Name"
                          className="customSelectWrapper"
                          layout="vertical"
                        >
                          <Select
                            key={`channelName-reset-${resetCounter}`}
                            showSearch
                            allowClear
                            className={`customSelect ${musicVendorStyles.selectField}`}
                            placeholder="Any channel name"
                            value={filters.channelName}
                            onChange={value => handleFilterChange('channelName', value)}
                            options={filterOptions.channelNames.map(name => ({ label: name, value: name }))}
                          />
                        </FormItem>
                      </Col>
                      <Col md={4}>
                        <FormItem
                          label="Status"
                          className="customSelectWrapper"
                          layout="vertical"
                        >
                          <Select
                            key={`status-reset-${resetCounter}`}
                            allowClear
                            className={`customSelect ${musicVendorStyles.selectField}`}
                            placeholder="All Status"
                            value={filters.status}
                            onChange={value => handleFilterChange('status', value)}
                            options={filterOptions.status.map(status => ({ label: status, value: status }))}
                          />
                        </FormItem>
                      </Col>
                      <Col md={4}>
                        <FormItem
                          label="Type"
                          className="customSelectWrapper"
                          layout="vertical"
                        >
                          <Select
                            key={`type-reset-${resetCounter}`}
                            allowClear
                            className={`customSelect ${musicVendorStyles.selectField}`}
                            placeholder="All"
                            value={filters.type}
                            onChange={value => handleFilterChange('type', value)}
                            options={filterOptions.types.map(type => ({ label: type, value: type }))}
                          />
                        </FormItem>
                      </Col>
                    </Row>
                  </Col>
                </Row>
              </Col>
            </Row>
          </BootstrapForm>

          {/* Channel Cards Section */}
          <Spin spinning={isLoading || isFetching} indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}>
          <div style={{ marginTop: '32px' }}>
            <Row style={{ justifyContent: 'center' }}>
              {filteredChannels.map(c => (
                <Col key={c.id} xs={12} sm={6} md={3} lg={3} xl={3} style={{ marginBottom: '16px' }}>
                  <Card
                    className={styles.rounded12}
                    style={{
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      border: '2px solid transparent',
                      height: '200px',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                      boxShadow: '0 4px 8px rgba(0,0,0,0.15)'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = '#3498db';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(52,152,219,0.2)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = 'transparent';
                      e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)';
                    }}
                    onClick={() => navigate(`/channel-programming/${c.id}`)}
                  >
                    <img
                      src={getChannelArtwork(c.artwork)}
                      alt={c.name}
                      style={{
                        width: 80,
                        height: 80,
                        borderRadius: '50%',
                        objectFit: 'cover',
                        marginBottom: 16
                      }}
                    />
                    <h3 style={{
                      color: '#2c3e50',
                      marginBottom: 8,
                      fontSize: 18
                    }}>
                      {c.name}
                    </h3>
                    <p style={{
                      color: '#7f8c8d',
                      fontSize: 12,
                      textAlign: 'center'
                    }}>
                      {c.description}
                    </p>
                    <div style={{
                      marginTop: 12,
                      padding: '4px 12px',
                      backgroundColor: c.status === 'LIVE' ? '#e8f5e8' : '#fff3cd',
                      color: c.status === 'LIVE' ? '#2d5a2d' : '#856404',
                      borderRadius: 12,
                      fontSize: 10,
                      fontWeight: '600'
                    }}>
                      {c.status}
                    </div>
                  </Card>
                </Col>
              ))}
            </Row>
          </div>
          </Spin>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default ChannelProgrammingOverview;
